import { useState, useEffect } from 'react';
import { Upload, Trash2, Copy, Image, FileText, File, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { attachmentService, operationLogService } from '@/services/storage';
import type { Attachment } from '@/types';
import { toast } from 'sonner';

export default function AdminAttachments() {
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    loadAttachments();
  }, []);

  const loadAttachments = async () => {
    setAttachments(await attachmentService.getAll());
  };

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);
    
    for (const file of Array.from(files)) {
      const type: 'image' | 'document' | 'other' = file.type.startsWith('image/') 
        ? 'image' 
        : file.type.includes('pdf') || file.type.includes('word') 
          ? 'document' 
          : 'other';
      
      try {
        await attachmentService.upload(file, type);
        await operationLogService.add({
          user: 'admin',
          action: '上传附件',
          module: '附件管理',
          details: `上传文件: ${file.name}`,
        });
      } catch (error) {
        toast.error(`上传失败: ${file.name}`);
      }
    }
    
    setUploading(false);
    loadAttachments();
    toast.success('上传完成');
  };

  const handleDelete = async (id: string) => {
    if (confirm('确定要删除此附件吗？')) {
      const att = attachments.find(a => a.id === id);
      await attachmentService.delete(id);
      await operationLogService.add({
        user: 'admin',
        action: '删除附件',
        module: '附件管理',
        details: `删除文件: ${att?.name || id}`,
      });
      loadAttachments();
      toast.success('附件已删除');
    }
  };

  const handleCopyUrl = async (url: string) => {
    navigator.clipboard.writeText(url);
    toast.success('链接已复制到剪贴板');
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  const getFileIcon = (type: string) => {
    switch (type) {
      case 'image':
        return <Image className="h-5 w-5 text-blue-500" />;
      case 'document':
        return <FileText className="h-5 w-5 text-green-500" />;
      default:
        return <File className="h-5 w-5 text-gray-500" />;
    }
  };

  const filteredAttachments = attachments.filter(att =>
    att.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-3xl font-bold">附件管理</h1>
        <div className="flex items-center space-x-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="搜索附件..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 w-64"
            />
          </div>
          <div className="relative">
            <Input
              type="file"
              multiple
              onChange={handleUpload}
              className="absolute inset-0 opacity-0 cursor-pointer"
              disabled={uploading}
            />
            <Button disabled={uploading}>
              <Upload className="mr-2 h-5 w-5" />
              {uploading ? '上传中...' : '上传文件'}
            </Button>
          </div>
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>预览</TableHead>
                <TableHead>文件名</TableHead>
                <TableHead>类型</TableHead>
                <TableHead>大小</TableHead>
                <TableHead>上传时间</TableHead>
                <TableHead className="text-right">操作</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredAttachments.map((attachment) => (
                <TableRow key={attachment.id}>
                  <TableCell>
                    {attachment.type === 'image' ? (
                      <img
                        src={attachment.url}
                        alt={attachment.name}
                        className="w-12 h-12 object-cover rounded"
                      />
                    ) : (
                      <div className="w-12 h-12 bg-gray-100 rounded flex items-center justify-center">
                        {getFileIcon(attachment.type)}
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="font-medium">{attachment.name}</TableCell>
                  <TableCell>
                    <span className="capitalize">{attachment.type}</span>
                  </TableCell>
                  <TableCell>{formatFileSize(attachment.size)}</TableCell>
                  <TableCell>
                    {new Date(attachment.uploadTime).toLocaleString()}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end space-x-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleCopyUrl(attachment.url)}
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(attachment.id)}
                      >
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {filteredAttachments.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              {searchTerm ? '未找到匹配的附件' : '暂无附件数据'}
            </div>
          )}
        </CardContent>
      </Card>

      <div className="mt-6 bg-blue-50 p-4 rounded-lg">
        <p className="text-sm text-blue-800">
          <strong>提示：</strong>当前为前端演示版本，附件以Base64格式存储在浏览器本地存储中。
          实际使用时建议配置云存储服务（如阿里云OSS、腾讯云COS等）。
        </p>
      </div>
    </div>
  );
}
